import React, { useState, useEffect, lazy, Suspense } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { useBilling } from '../../contexts/BillingContext';
import { useDisplayMode } from '../../contexts/DisplayModeContext';
import { getCurrencySymbol } from '../../utils/currency';
import { isIOSNative } from '../../utils/nativeBridge';
import { NotificationCenter } from '../notifications/NotificationGovernance';
import { EmailVerificationBanner } from '../EmailVerificationBanner';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import { useMyAccess } from '../../hooks/useMyAccess';
import { usePartnerPortalLink } from '../../hooks/usePartnerPortalLink';
// Lazy: keeps react-markdown/remark-gfm out of the main bundle (older-browser
// resilience + smaller first paint).
const AICopilotWidget = lazy(() => import('../copilot/AICopilotWidget'));
// iter959 Phase 3 - persistent banner for backlog / refresh jobs
import BacklogProgressBanner from '../banking/BacklogProgressBanner';
import MonoCatchUpBanner from '../banking/MonoCatchUpBanner';
// iter1207 - global backlog fee confirmation modal (Plaid + Mono)
import GlobalBacklogModalProvider from '../banking/GlobalBacklogModalProvider';
// iter1230 - global floating card that surfaces statement-upload progress
// on EVERY authenticated page (not just Bank Feed Center).
import GlobalUploadProgressBadge from '../banking/GlobalUploadProgressBadge';
// iter1230 - global modal for the statement-upload backlog-fee stage.
// The banner above handles the POST-confirmation processing phase;
// this modal handles the pre-processing confirmation on every page.
import GlobalUploadBacklogModalProvider from '../banking/GlobalUploadBacklogModalProvider';
import GlobalStartDateDecisionProvider from '../banking/GlobalStartDateDecisionProvider';
import GlobalSubAccountDecisionProvider from '../banking/GlobalSubAccountDecisionProvider';
import GlobalStatementContinuityGapProvider from '../banking/GlobalStatementContinuityGapProvider';
import MissingRowsDialogHost from '../bankfeed/MissingRowsDialog';
// iter1213 - persistent, non-dismissible task strip for accounts whose
// Plaid opening balance was deferred by the completeness gates.
import PendingOpeningBalanceTaskBar from '../banking/PendingOpeningBalanceTaskBar';
// iter959 - MobileKeyboardDebugOverlay unmounted after root-cause of the
// mobile-keyboard collapse was identified & fixed in CreateBillPage/CreateInvoicePage.
// Leaving the file in the repo for future diagnostics; not imported here.
import { 
  Handshake,
  Home, 
  FileText, 
  Receipt, 
  Building2, 
  BarChart3, 
  MessageSquare, 
  Settings,
  LogOut,
  Menu,
  X,
  Calculator,
  Package,
  PieChart,
  Brain,
  CalendarCheck,
  Shield,
  Target,
  Wallet,
  ArrowLeftRight,
  TrendingUp,
  CreditCard,
  Users,
  Sparkles,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GlobalSearchTrigger, GlobalSearchHost } from '../GlobalSearch';
import BackButton from '../ui/back-button';
import { BusinessSwitcher } from './BusinessSwitcher';
import { GlobalBillingBanner } from '../BillingAccessGate';
import { BackToTop } from '../BackToTop';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
// OpeningPositionBanner removed - unified to single pathway via OpeningPositionWidget on dashboard

const LOGO_ICON = "/akountfly-icon.png";
const LOGO_FULL = "/akountfly-logo-full.png";

// Navigation items - `labelKey` is the canonical (accountant-spelled) name
// passed to `getLabel()` so the sidebar reads naturally in either Business
// or Accountant mode. See `contexts/displayLabels.js` for the mapping.
const businessNavItems = [
  { path: '/dashboard',         labelKey: 'Home',                  icon: Home },
  { path: '/invoices',          labelKey: 'Invoices',              icon: FileText },
  { path: '/bills',             labelKey: 'Bills',                 icon: Receipt },
  { path: '/insights',          labelKey: 'Insights',              icon: BarChart3 },
  { path: '/akountfly-ai',      labelKey: 'AI CFO',                icon: MessageSquare },
  { path: '/bank-feeds',        labelKey: 'Transactions',          icon: Brain },
  { path: '/banks',             labelKey: 'Bank Accounts',         icon: Building2 },
  { path: '/reports',           labelKey: 'Reports',               icon: PieChart },
  { path: '/budget',            labelKey: 'Budgets',               icon: Target },
  { path: '/products-services', labelKey: 'Products & Customers',  icon: Package },
  { path: '/advanced-view-hub', labelKey: 'More Tools',            icon: Calculator, badge: 'Pro' },
];

// Navigation items for PERSONAL FINANCE mode
const personalNavItems = [
  { path: '/personal',          labelKey: 'Personal Dashboard',    icon: Home },
  { path: '/invoices',          labelKey: 'Income',                icon: TrendingUp },
  { path: '/bills',             labelKey: 'Expenses',              icon: CreditCard },
  { path: '/personal/budget',   labelKey: 'Personal Budget',       icon: Target },
  { path: '/bank-feeds',        labelKey: 'Transactions',          icon: Brain },
  { path: '/banks',             labelKey: 'Bank Accounts',         icon: Building2 },
  { path: '/products-services', labelKey: 'Sources & Payees',      icon: Package },
  { path: '/personal/reports',  labelKey: 'Personal Reports',      icon: PieChart },
  { path: '/akountfly-ai',      labelKey: 'AI CFO',                icon: MessageSquare },
  { path: '/personal/settings', labelKey: 'Account Settings',      icon: Shield },
];

const SIDEBAR_KEY = 'akf_sidebar_collapsed';

// One nav row that works in both the full sidebar and the collapsed icon rail.
const RailLink = ({ to, icon: Icon, label, collapsed, testId, activeClass, inactiveClass, dot, children }) => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger asChild>
      <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
          `relative flex items-center gap-3 py-2 [@media(max-height:900px)]:py-1.5 rounded-xl transition-all duration-200 ${collapsed ? 'justify-center px-0' : 'px-3'} ${
            isActive ? activeClass : (inactiveClass || 'text-muted-foreground hover:bg-muted hover:text-foreground')
          }`
        }
      >
        <Icon className="w-5 h-5 shrink-0" />
        {!collapsed && <span className="font-medium text-sm flex-1 truncate">{label}</span>}
        {!collapsed && children}
        {collapsed && dot && <span className="absolute top-1.5 right-2.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-card" />}
      </NavLink>
    </TooltipTrigger>
    {collapsed && <TooltipContent side="right" className="text-xs font-medium">{label}</TooltipContent>}
  </Tooltip>
);

export const DashboardLayout = ({ children }) => {
  const { user, business, logout } = useAuth();
  const partnerLink = usePartnerPortalLink(user);
  
  // SEPARATE PRODUCTS: Use account_type from user to determine which product
  const accountType = user?.account_type || 'business';
  const isPersonalAccount = accountType === 'personal';
  
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => setSidebarCollapsed((c) => {
    try { localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1'); } catch { /* ignore */ }
    return !c;
  });
  const isAdminUser = user?.is_platform_admin === true;
  const { billingStatus } = useBilling() || {};
  const { counts: activityCounts, clearCount } = useActivityTracker();
  // Unified view: getLabel() returns the single canonical label for every
  // navigation key - no per-user mode switching. See contexts/displayLabels.js
  const { getLabel } = useDisplayMode();

  // Map sidebar paths to activity module keys
  const pathToModule = {
    '/bank-feeds': 'bank_feeds',
    '/invoices': 'invoices',
    '/bills': 'bills',
  };

  // Clear sidebar badge count (UI only) when user navigates to the module page.
  // The actual server-side markSeen is handled by each page on unmount,
  // so that row-level "NEW" badges can use the pre-visit lastSeen timestamp.
  useEffect(() => {
    const module = pathToModule[location.pathname];
    if (module && activityCounts[module] > 0) {
      clearCount(module);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trial countdown
  const trialDaysLeft = (() => {
    if (billingStatus?.status !== 'trialing' || !billingStatus?.trial_ends_at) return null;
    const diff = new Date(billingStatus.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  // Choose nav items based on account type (Business vs Personal - SEPARATE PRODUCTS)
  const navItems = isPersonalAccount ? personalNavItems : businessNavItems;

  // iter 1006 - role-aware navigation for invited team members.
  // Owners (and personal accounts) always see everything; members only see
  // modules their role has at least one permission for. Backend enforces too.
  const { access, isMember, hasPermission } = useMyAccess();
  const NAV_PERMISSIONS = {
    '/dashboard': ['view_dashboards'],
    '/invoices': ['create_invoices', 'edit_invoices', 'delete_invoices'],
    '/bills': ['create_bills', 'edit_bills', 'delete_bills'],
    '/insights': ['view_dashboards'],
    '/akountfly-ai': ['view_dashboards'],
    '/bank-feeds': ['view_bank_feeds'],
    '/banks': ['view_bank_feeds', 'manage_banking'],
    '/reports': ['view_reports'],
    '/budget': ['manage_budgets'],
    '/products-services': ['manage_contacts', 'manage_products'],
    '/advanced-view-hub': ['create_journals', 'manage_fixed_assets', 'payroll_view', 'manage_taxes'],
  };
  const visibleNavItems = (!isPersonalAccount && isMember)
    ? navItems.filter((item) => {
        const req = NAV_PERMISSIONS[item.path];
        return !req || req.some((p) => hasPermission(p));
      })
    : navItems;
  void access;
  // Mobile: items that are not among the 5 bottom tabs surface their counts on the hamburger.
  const hiddenTabsCount = visibleNavItems.slice(5).reduce((sum, item) => {
    const mod = pathToModule[item.path];
    return sum + (mod ? (activityCounts[mod] || 0) : 0);
  }, 0);

  const handleLogout = () => {
    logout();
  };

  // Mode switching is no longer needed - SEPARATE PRODUCTS ARCHITECTURE
  // Users must login with different accounts to switch between Business and Personal

  const getBusinessCurrencySymbol = () => {
    return getCurrencySymbol(business?.currency || 'NGN');
  };

  // Get display name and info based on account type - SEPARATE PRODUCTS
  const getEntityDisplayInfo = () => {
    if (isPersonalAccount) {
      return {
        name: 'Personal Finance',
        subtitle: 'Personal',
        initial: user?.full_name?.charAt(0) || 'P',
        gradientFrom: '#108981',
        gradientTo: '#0a6b65'
      };
    }
    return {
      name: business?.business_name || 'Business',
      subtitle: business?.industry || 'Business',
      initial: business?.business_name?.charAt(0) || 'B',
      gradientFrom: '#283F8B',
      gradientTo: '#108981'
    };
  };

  const entityInfo = getEntityDisplayInfo();

  return (
    <TooltipProvider>
    <div className="min-h-dvh bg-background">
      {/* Desktop Sidebar — collapsible to a slim icon rail (remembered per device) */}
      <aside
        data-testid="desktop-sidebar"
        data-collapsed={sidebarCollapsed ? 'true' : 'false'}
        className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col bg-card border-r border-border transition-[width] duration-300 ease-in-out ${sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-64'}`}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          data-testid="sidebar-collapse-toggle"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="absolute -right-3.5 top-[68px] z-40 w-7 h-7 rounded-full border border-border bg-card shadow-md flex items-center justify-center text-muted-foreground hover:bg-[#283F8B] hover:text-white hover:border-[#283F8B] hover:scale-110 active:scale-95 transition-[background-color,color,transform,border-color] duration-200"
        >
          {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        <div className="flex flex-col h-screen">
          <div className={`py-2 border-b border-border flex-shrink-0 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-2 py-1">
                <Link to="/dashboard" aria-label="Go to Home" data-testid="global-logo-link" className="rounded-md hover:opacity-80 transition-opacity">
                  <img src={LOGO_ICON} alt="AkountFly" className="w-9 h-9 object-contain" />
                </Link>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={toggleSidebar}
                      data-testid="sidebar-rail-entity"
                      className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
                      style={{ background: `linear-gradient(135deg, ${entityInfo.gradientFrom}, ${entityInfo.gradientTo})` }}
                    >
                      <span className="text-white font-bold text-sm">{entityInfo.initial}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs">{entityInfo.name} · click to expand</TooltipContent>
                </Tooltip>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <Link to="/dashboard" aria-label="Go to Home" data-testid="global-logo-link" className="flex items-center gap-2 min-w-0 rounded-md hover:opacity-80 transition-opacity">
                    <img src={LOGO_FULL} alt="AkountFly" className="max-w-[112px] h-auto object-contain shrink-0" />
                  </Link>
                  <div className="flex items-center gap-0.5 -mr-1.5 shrink-0" data-testid="sidebar-header-icons">
                    <GlobalSearchTrigger className="w-7 h-7 !p-0 flex items-center justify-center" iconClassName="w-4 h-4" />
                    <Link
                      to="/help"
                      data-testid="open-help-center"
                      aria-label="Help Center"
                      title="Help Center"
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <BookOpen className="w-4 h-4" />
                    </Link>
                    {partnerLink.is_partner && (
                      <Link to="/partner" data-testid="open-partner-portal" aria-label="Partner Portal" title={`Partner Portal · ${partnerLink.partner_name || ''}`}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#108981] hover:bg-[#108981]/10 transition-colors">
                        <Handshake className="w-4 h-4" />
                      </Link>
                    )}
                    <NotificationCenter variant="desktop" compact />
                  </div>
                </div>
                {isPersonalAccount ? (
                  <div className="mt-2 flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${entityInfo.gradientFrom}, ${entityInfo.gradientTo})` }}
                    >
                      <span className="text-white font-bold text-sm">{entityInfo.initial}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate text-sm">{entityInfo.name}</p>
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-muted-foreground">{entityInfo.subtitle}</p>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-[#108981]/30 text-[#108981]">
                          Personal
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <BusinessSwitcher business={business} entityInfo={entityInfo} variant="sidebar" />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Navigation - Scrollable */}
          <nav className={`flex-1 min-h-0 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden [@media(max-height:900px)]:space-y-0 ${sidebarCollapsed ? 'px-2' : 'px-3'}`} data-testid="sidebar-nav">
            {visibleNavItems.map((item) => {
              const label = getLabel(item.labelKey);
              const mod = pathToModule[item.path];
              const count = mod ? activityCounts[mod] : 0;
              return (
                <RailLink
                  key={item.path}
                  to={item.path}
                  icon={item.icon}
                  label={label}
                  collapsed={sidebarCollapsed}
                  dot={count > 0}
                  testId={`nav-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  activeClass="bg-[#283F8B] text-white shadow-lg"
                >
                  {count > 0 && (
                    <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                  {item.badge && (
                    <Badge variant="secondary" className="bg-[#5FC6C5]/20 text-[#108981] text-[10px] px-1.5 py-0">
                      {item.badge}
                    </Badge>
                  )}
                </RailLink>
              );
            })}
          </nav>

          {/* Bottom Section - Fixed at bottom */}
          <div className={`mt-auto py-2 border-t border-border space-y-0.5 [@media(max-height:900px)]:space-y-0 flex-shrink-0 ${sidebarCollapsed ? 'px-2' : 'px-3'}`} data-testid="sidebar-account-section">
            <RailLink to="/team" icon={Users} label="Team" collapsed={sidebarCollapsed} testId="nav-team" activeClass="bg-[#283F8B] text-white" />

            {isAdminUser && (
              <RailLink to="/admin" icon={Shield} label="Admin Panel" collapsed={sidebarCollapsed} testId="nav-admin-panel"
                activeClass="bg-red-500/10 text-red-400" inactiveClass="text-red-400/60 hover:bg-red-500/10 hover:text-red-400" />
            )}

            {/* Feb 2026 · iOS wrapper users see a read-only Billing page
                (plan + usage), but plan-management + card actions are
                hidden inside the page itself — the link is safe to show. */}
            <RailLink
              to="/billing"
              icon={Sparkles}
              label={isIOSNative() ? 'Plan & Usage' : 'Plan & Billing'}
              collapsed={sidebarCollapsed}
              testId="nav-billing-link"
              dot={trialDaysLeft !== null}
              activeClass="bg-[#283F8B] text-white"
              inactiveClass={trialDaysLeft !== null ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : undefined}
            >
              {trialDaysLeft !== null && (
                <Badge
                  data-testid="trial-countdown-badge"
                  className={`text-[10px] px-1.5 py-0 h-5 border-0 font-semibold ${
                    trialDaysLeft <= 2
                      ? 'bg-red-500 text-white'
                      : 'bg-amber-500 text-white'
                  }`}
                >
                  {trialDaysLeft}d left
                </Badge>
              )}
            </RailLink>
            <RailLink to="/settings" icon={Settings} label="Settings" collapsed={sidebarCollapsed} testId="nav-settings-link" activeClass="bg-[#283F8B] text-white" />
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className={`flex items-center gap-3 py-2 [@media(max-height:900px)]:py-1.5 rounded-xl w-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-200 ${sidebarCollapsed ? 'justify-center px-0' : 'px-3'}`}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  {!sidebarCollapsed && <span className="font-medium text-sm">Log out</span>}
                </button>
              </TooltipTrigger>
              {sidebarCollapsed && <TooltipContent side="right" className="text-xs">Log out</TooltipContent>}
            </Tooltip>

            {/* Legal Links */}
            {!sidebarCollapsed && (
              <div className="pt-2 mt-1.5 border-t border-border/50">
                <div className="flex items-center gap-4 px-3 text-xs text-muted-foreground">
                  <a href="/privacy-policy" target="_blank" className="hover:text-foreground transition-colors">Privacy</a>
                  <span>•</span>
                  <a href="/terms-of-service" target="_blank" className="hover:text-foreground transition-colors">Terms</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Header - Native-style frozen top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
        <div className="flex items-center justify-between px-3 py-3 min-h-[64px]">
          <div className="flex items-center gap-2 min-w-0">
            {location.pathname !== '/dashboard' && (
              <BackButton fallbackPath="/dashboard" className="h-10 w-10 shrink-0" data-testid="global-back-button-mobile" />
            )}
            <Link to="/dashboard" aria-label="Go to Home" data-testid="global-logo-link-mobile" className="shrink-0 active:opacity-70 transition-opacity">
              <img
                src={LOGO_FULL}
                alt="AkountFly"
                className="h-7 w-auto object-contain shrink-0"
                data-testid="global-logo-icon-mobile"
              />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {/* iter971k-T · Direct link to the full Help Center (mobile). */}
            <GlobalSearchTrigger className="w-10 h-10 !p-0 flex items-center justify-center" />
            <Link
              to="/help"
              data-testid="open-help-center-mobile"
              aria-label="Help Center"
              className="w-10 h-10 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <BookOpen className="w-5 h-5" />
            </Link>
            {partnerLink.is_partner && (
              <Link to="/partner" data-testid="open-partner-portal-mobile" aria-label="Partner Portal"
                className="w-10 h-10 rounded-lg flex items-center justify-center text-[#108981] hover:bg-[#108981]/10 transition-colors">
                <Handshake className="w-5 h-5" />
              </Link>
            )}
            {/* Notification Center (Bell Icon) - Made larger for mobile */}
            <NotificationCenter variant="mobile" />
            <button
              className="relative inline-flex items-center justify-center w-[45px] h-[45px] rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen
                ? <X style={{ width: '29px', height: '29px' }} strokeWidth={2.25} />
                : <Menu style={{ width: '29px', height: '29px' }} strokeWidth={2.25} />
              }
              {!mobileMenuOpen && hiddenTabsCount > 0 && (
                <span
                  className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-card"
                  data-testid="mobile-menu-count"
                >
                  {hiddenTabsCount > 99 ? '99+' : hiddenTabsCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-[64px] left-0 right-0 bg-card border-b border-border shadow-lg animate-fadeIn max-h-[calc(100dvh-64px-4rem-env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain" data-testid="mobile-nav-drawer">
            <nav className="p-4 space-y-1 pb-6">
              {!isPersonalAccount && (
                <div className="pb-2 mb-2 border-b border-border">
                  <BusinessSwitcher business={business} entityInfo={entityInfo} variant="mobile" />
                </div>
              )}
              {visibleNavItems.map((item) => {
                const mobileMod = pathToModule[item.path];
                const mobileCount = mobileMod ? activityCounts[mobileMod] : 0;
                return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-[#283F8B] text-white'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium flex-1">{getLabel(item.labelKey)}</span>
                  {mobileCount > 0 && (
                    <span
                      className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5"
                      data-testid={`mobile-nav-count-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {mobileCount > 99 ? '99+' : mobileCount}
                    </span>
                  )}
                  {item.badge && (
                    <Badge variant="secondary" className="bg-[#5FC6C5]/20 text-[#108981] text-[10px] px-1.5 py-0" data-testid={`mobile-nav-badge-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.badge}</Badge>
                  )}
                </NavLink>
                );
              })}
              <NavLink
                to="/team"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#283F8B] text-white'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Team</span>
              </NavLink>
              <NavLink
                to="/billing"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#283F8B] text-white'
                      : trialDaysLeft !== null
                        ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-medium flex-1">{isIOSNative() ? 'Plan & Usage' : 'Plan & Billing'}</span>
                {trialDaysLeft !== null && (
                  <Badge className={`text-[10px] px-1.5 py-0 h-5 border-0 font-semibold ${trialDaysLeft <= 2 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>
                    {trialDaysLeft}d left
                  </Badge>
                )}
              </NavLink>
              <NavLink
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-[#283F8B] text-white'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </NavLink>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl w-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-200"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Log out</span>
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation — safe-area aware (iter1102). Container gets
          system-inset padding so labels & tap-targets sit above the iOS home
          indicator / Android gesture pill / 3-button nav. */}
      <nav data-mobile-bottom-nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16">
          {visibleNavItems.slice(0, 5).map((item) => {
            const label = getLabel(item.labelKey);
            const tabMod = pathToModule[item.path];
            const tabCount = tabMod ? activityCounts[tabMod] : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`mobile-tab-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={({ isActive }) =>
                  `relative flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'text-[#283F8B]'
                      : 'text-muted-foreground'
                  }`
                }
              >
                <item.icon style={{ width: '22px', height: '22px' }} />
                {tabCount > 0 && (
                  <span
                    className="absolute top-0.5 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-card"
                    data-testid={`mobile-tab-count-${item.labelKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {tabCount > 99 ? '99+' : tabCount}
                  </span>
                )}
                {/* iter1102 - show full label (previously first word only,
                    which mangled "AI CFO" to "AI"). */}
                <span className="text-[11px] font-medium leading-tight truncate max-w-[64px]">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Main Content — bottom padding reserves nav height + system inset. */}
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'} transition-[margin] duration-300 ease-in-out pt-[64px] lg:pt-0 pb-[calc(4rem+env(safe-area-inset-bottom)+1rem)] lg:pb-0 min-h-dvh overflow-x-hidden`}>
        <GlobalBillingBanner />
        {/* iter971k-D · Global Mono catch-up banner - compact, non-blocking,
            data-driven so it survives logout/login. */}
        <MonoCatchUpBanner />
        {/* Email Verification Banner */}
        <EmailVerificationBanner />
        {/* iter959 Phase 3 - persistent progress banner for backlog + refresh jobs */}
        <GlobalSearchHost />
        <BacklogProgressBanner />
        {/* iter1213 - mandatory Opening Balance confirmation strip. Persists
            until every affected account has its OB posted. */}
        <PendingOpeningBalanceTaskBar />
        {/* iter1207 - global backlog fee confirmation modal - mounts here
            so the prompt re-surfaces on any page, not only /banks. */}
        <GlobalBacklogModalProvider />
        {/* iter1230 - global floating upload-progress card. Persists on
            EVERY authenticated page until every in-flight statement
            upload finishes, closing the visibility gap that stranded
            uploads started from /banks. Shows only jobs in the
            processing phase - awaiting_confirmation is handled by the
            sibling modal below. */}
        <GlobalUploadProgressBadge />
        {/* iter1230 - global backlog-fee confirmation modal for
            statement uploads. Surfaces on every page whenever a job is
            paused at awaiting_confirmation so the user can decide
            whether to pay (or accept the free-allowance draw-down)
            BEFORE the transactions are pushed into Bank Feeds. */}
        <GlobalUploadBacklogModalProvider />
        <GlobalStartDateDecisionProvider />
        <GlobalSubAccountDecisionProvider />
        {/* iter1235 - Global continuity-gap dialog. When a statement
            fails the balance/date continuity check, we surface a rich
            modal (instead of a generic toast) that explains exactly
            which statement is missing and how to fix it. */}
        <GlobalStatementContinuityGapProvider />
        <MissingRowsDialogHost />
        {/* Global back button is now docked in the top bar (mobile header +
            desktop sidebar). No floating "<" here to avoid overlapping content. */}
        {children}
      </main>
      <BackToTop />
      {/* iter971k-U · Phase 3 - Floating AI Co-pilot available on every page */}
      <Suspense fallback={null}>
        <AICopilotWidget />
      </Suspense>
    </div>
    </TooltipProvider>
  );
};

export default DashboardLayout;
